import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  Skeleton,
  Alert,
  Chip,
  List,
  ListItem,
  ListItemText,
  Divider,
  Collapse,
  IconButton
} from '@mui/material';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip as RechartsTooltip,
  ResponsiveContainer,
  Legend
} from 'recharts';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import TrendingDownIcon from '@mui/icons-material/TrendingDown';
import TrendingFlatIcon from '@mui/icons-material/TrendingFlat';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import { getPointDrift } from '../../utils/analyticsApi';

const COLORS = [
  '#3B82F6', '#10B981', '#F59E0B', '#EF4444', '#8B5CF6',
  '#EC4899', '#06B6D4', '#84CC16', '#F97316', '#6366F1'
];

const PointDriftChart = ({ scope, organizationId, teamId }) => {
  const [driftData, setDriftData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [expandedItem, setExpandedItem] = useState(null);

  useEffect(() => {
    fetchDriftData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [scope, organizationId, teamId]);

  const fetchDriftData = async () => {
    setLoading(true);
    setError(null);
    try {
      const params = {};
      if (organizationId) params.organizationId = organizationId;
      if (teamId) params.teamId = teamId;
      const response = await getPointDrift(params);
      setDriftData(response.data.driftData || []);
    } catch (err) {
      console.error('Error fetching drift data:', err);
      setError('Failed to load point drift data');
    } finally {
      setLoading(false);
    }
  };

  const getDriftIcon = (direction) => {
    switch (direction) {
      case 'up':
        return <TrendingUpIcon sx={{ color: '#EF4444' }} />;
      case 'down':
        return <TrendingDownIcon sx={{ color: '#3B82F6' }} />;
      default:
        return <TrendingFlatIcon sx={{ color: '#10B981' }} />;
    }
  };

  const getDriftColor = (direction) => {
    switch (direction) {
      case 'up':
        return { bg: '#FEE2E2', text: '#991B1B' };
      case 'down':
        return { bg: '#DBEAFE', text: '#1E40AF' };
      default:
        return { bg: '#D1FAE5', text: '#065F46' };
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric'
    });
  };

  const formatDateLong = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    });
  };

  const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
      return (
        <Paper sx={{ p: 1.5 }}>
          <Typography variant="subtitle2">{formatDateLong(label)}</Typography>
          {payload.map((entry, index) => (
            <Typography
              key={index}
              variant="body2"
              sx={{ color: entry.color }}
            >
              {entry.name}: {entry.value}
            </Typography>
          ))}
        </Paper>
      );
    }
    return null;
  };

  // Prepare chart data - combine all histories into a timeline
  const prepareChartData = () => {
    if (driftData.length === 0) return [];

    // Get all unique dates across all items
    const allDates = new Set();
    driftData.forEach(item => {
      item.history.forEach(h => {
        allDates.add(new Date(h.date).toISOString().split('T')[0]);
      });
    });

    // Sort dates
    const sortedDates = Array.from(allDates).sort();

    // Create data points for each date
    return sortedDates.map(dateStr => {
      const point = { date: dateStr };
      driftData.forEach(item => {
        const historyItem = item.history.find(
          h => new Date(h.date).toISOString().split('T')[0] === dateStr
        );
        if (historyItem) {
          point[item.jiraKey] = historyItem.numericEstimate;
        }
      });
      return point;
    });
  };

  if (loading) {
    return (
      <Box>
        <Typography variant="h6" gutterBottom>
          Point Drift Over Time
        </Typography>
        <Skeleton variant="rectangular" height={300} />
      </Box>
    );
  }

  if (error) {
    return (
      <Alert severity="error" sx={{ mb: 2 }}>
        {error}
      </Alert>
    );
  }

  if (driftData.length === 0) {
    return (
      <Box sx={{ textAlign: 'center', py: 4 }}>
        <Typography variant="body1" color="text.secondary">
          No items have been estimated multiple times yet
        </Typography>
        <Typography variant="body2" color="text.disabled" sx={{ mt: 1 }}>
          Re-estimate items across different sessions to see how estimates change over time
        </Typography>
      </Box>
    );
  }

  const chartData = prepareChartData();

  return (
    <Box>
      <Typography variant="h6" gutterBottom>
        Point Drift Over Time
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        Track how estimates for the same items change across sessions
      </Typography>

      {chartData.length > 0 && driftData.length <= 10 && (
        <Paper sx={{ p: 2, mb: 3 }}>
          <ResponsiveContainer width="100%" height={300}>
            <LineChart data={chartData} margin={{ top: 10, right: 30, left: 0, bottom: 0 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
              <XAxis
                dataKey="date"
                tick={{ fill: '#64748B', fontSize: 12 }}
                axisLine={{ stroke: '#CBD5E1' }}
                tickFormatter={formatDate}
              />
              <YAxis
                tick={{ fill: '#64748B', fontSize: 12 }}
                axisLine={{ stroke: '#CBD5E1' }}
              />
              <RechartsTooltip content={<CustomTooltip />} />
              <Legend />
              {driftData.map((item, index) => (
                <Line
                  key={item.jiraKey}
                  type="monotone"
                  dataKey={item.jiraKey}
                  name={item.jiraKey}
                  stroke={COLORS[index % COLORS.length]}
                  strokeWidth={2}
                  dot={{ r: 4 }}
                  connectNulls
                />
              ))}
            </LineChart>
          </ResponsiveContainer>
        </Paper>
      )}

      {driftData.length > 10 && (
        <Alert severity="info" sx={{ mb: 2 }}>
          Chart hidden due to large number of items ({driftData.length}). See detailed list below.
        </Alert>
      )}

      {/* Detailed list */}
      <Paper sx={{ overflow: 'hidden' }}>
        <Typography variant="subtitle1" sx={{ p: 2, bgcolor: '#141414', fontWeight: 600 }}>
          Items with Estimate Changes ({driftData.length})
        </Typography>
        <List disablePadding>
          {driftData.map((item, index) => {
            const colors = getDriftColor(item.direction);
            const isExpanded = expandedItem === item.jiraKey;

            return (
              <React.Fragment key={item.jiraKey}>
                <ListItem
                  sx={{
                    cursor: 'pointer',
                    '&:hover': { bgcolor: '#141414' }
                  }}
                  onClick={() => setExpandedItem(isExpanded ? null : item.jiraKey)}
                  secondaryAction={
                    <IconButton edge="end" size="small">
                      {isExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                    </IconButton>
                  }
                >
                  <ListItemText
                    primary={
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Chip
                          label={item.jiraKey}
                          size="small"
                          sx={{ fontWeight: 500 }}
                        />
                        <Typography variant="body2" sx={{ flex: 1 }}>
                          {item.title}
                        </Typography>
                      </Box>
                    }
                    secondary={
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.5 }}>
                        {getDriftIcon(item.direction)}
                        <Typography variant="body2" component="span">
                          {item.firstEstimate} → {item.latestEstimate}
                        </Typography>
                        <Chip
                          label={item.direction === 'up' ? `+${item.drift}` : item.drift}
                          size="small"
                          sx={{
                            height: 20,
                            fontSize: '0.7rem',
                            bgcolor: colors.bg,
                            color: colors.text
                          }}
                        />
                        <Typography variant="caption" color="text.secondary">
                          ({item.estimationCount} estimates)
                        </Typography>
                      </Box>
                    }
                  />
                </ListItem>

                <Collapse in={isExpanded}>
                  <Box sx={{ px: 3, py: 2, bgcolor: '#141414' }}>
                    <Typography variant="subtitle2" sx={{ mb: 1 }}>
                      Estimation History
                    </Typography>
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
                      {item.history.map((h, hIndex) => (
                        <Paper
                          key={hIndex}
                          sx={{
                            p: 1.5,
                            minWidth: 150,
                            display: 'flex',
                            flexDirection: 'column',
                            gap: 0.5
                          }}
                          variant="outlined"
                        >
                          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                            <Chip
                              label={h.estimate}
                              size="small"
                              color="primary"
                              sx={{ fontWeight: 600 }}
                            />
                            {hIndex > 0 && h.numericEstimate !== item.history[hIndex - 1].numericEstimate && (
                              <Typography
                                variant="caption"
                                sx={{
                                  color: h.numericEstimate > item.history[hIndex - 1].numericEstimate
                                    ? 'error.main'
                                    : 'info.main'
                                }}
                              >
                                {h.numericEstimate > item.history[hIndex - 1].numericEstimate ? '↑' : '↓'}
                              </Typography>
                            )}
                          </Box>
                          <Typography variant="caption" color="text.secondary">
                            {h.sessionName}
                          </Typography>
                          <Typography variant="caption" color="text.disabled">
                            {formatDateLong(h.date)}
                          </Typography>
                        </Paper>
                      ))}
                    </Box>
                  </Box>
                </Collapse>

                {index < driftData.length - 1 && <Divider />}
              </React.Fragment>
            );
          })}
        </List>
      </Paper>
    </Box>
  );
};

export default PointDriftChart;
